// The prompt editor shared by Admin › AI Prompts and the IT Budget Settings
// Console's Prompts tab: a list of prompts and their versions, a Draft edited
// beside a rendered preview, model and limits, the output schema, Discard,
// Activate (which retires the previous Active version; re-activating a Retired
// one is the rollback) and, when the prompt's module provides one, a Test.
// Each caller passes its own endpoints, so its own permissions apply.

// REACT IMPORTS
import React, { useCallback, useEffect, useMemo, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import {
	Alert, Button, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ScienceIcon from "@mui/icons-material/Science";

// LIBRARIES
import { marked } from "marked";
import DOMPurify from "dompurify";

// API, CONSTANTS
import { deleteAPICall, getAPICall, getBlobAPICall, patchAPICall, postAPICall } from "../../config/apiCalls";
import { VERSION_PILLS, apiError, fmtDay, pillStyle } from "../ITBudget/itBudgetUtils";

// CSS (the IT Budget screens' layout classes)
import "../ITBudget/ITBudget.css";

const schemaToText = (schema) => (schema ? JSON.stringify(schema, null, 2) : "");

// endpoints: {
//   list: url, listParams?: object, version(id), createDraft: url (POST {from_id}), draft(id) (PATCH / DELETE),
//   activate(id), test(id) (POST {subject_id}), subjects(prompt): url or null when the prompt has no test,
//   models: url, download(id)
// }
// promptMeta(prompt): an extra line on the prompt's card; promptActions(prompt): extra header buttons.
// singleKey: edit just that prompt (Admin › AI Prompts' Prompt Editor); the prompt list is hidden.
const PromptWorkspace = ({ endpoints, canEdit, listTitle = "Prompts", markdownHint = "", emptyText = "No prompts yet.", promptMeta, promptActions, reloadKey, selectKey, singleKey }) => {
	const { instance, accounts } = useMsal();
	const [prompts, setPrompts] = useState(null);
	const [models, setModels] = useState([]);
	const [promptKey, setPromptKey] = useState(null);
	const [versionId, setVersionId] = useState(null);
	const [version, setVersion] = useState(null);
	const [draft, setDraft] = useState(null); // editable copy of a Draft version
	const [schemaText, setSchemaText] = useState("");
	const [schemaOpen, setSchemaOpen] = useState(false);
	const [discardOpen, setDiscardOpen] = useState(false);
	const [subjects, setSubjects] = useState(null); // null: this prompt has no test
	const [subjectId, setSubjectId] = useState("");
	const [testResult, setTestResult] = useState(null);
	const [busy, setBusy] = useState("");
	const [error, setError] = useState("");

	const pick = (p) => {
		setPromptKey(p.prompt_key);
		setVersionId((p.draft_version || p.active_version || p.versions[0]).id);
	};

	const listParams = JSON.stringify(endpoints.listParams || {});
	const loadList = useCallback(
		async (keepKey) => {
			const res = await getAPICall(instance, accounts[0], endpoints.list, JSON.parse(listParams));
			const rows = singleKey ? res.data.filter((p) => p.prompt_key === singleKey) : res.data;
			setPrompts(rows);
			const current = rows.find((p) => p.prompt_key === keepKey) || rows[0];
			if (current) pick(current);
			else {
				setPromptKey(null);
				setVersionId(null);
				setVersion(null);
				setDraft(null);
			}
		},
		[instance, accounts, endpoints.list, listParams, singleKey],
	);

	useEffect(() => {
		loadList(selectKey || promptKey).catch((err) => setError(apiError(err, "Couldn't load prompts.")));
	}, [loadList, reloadKey]);

	useEffect(() => {
		getAPICall(instance, accounts[0], endpoints.models)
			.then((res) => setModels(res.data))
			.catch(() => {});
	}, [endpoints.models]);

	useEffect(() => {
		if (!versionId) return;
		setTestResult(null);
		getAPICall(instance, accounts[0], endpoints.version(versionId))
			.then((res) => {
				setVersion(res.data);
				setDraft(res.data.status === "draft" ? { ...res.data } : null);
				setSchemaText(schemaToText(res.data.output_schema));
			})
			.catch((err) => setError(apiError(err, "Couldn't load the prompt version.")));
	}, [versionId]);

	const current = (prompts || []).find((p) => p.prompt_key === promptKey);
	const shown = draft || version;
	const preview = useMemo(() => DOMPurify.sanitize(marked.parse(shown?.prompt_markdown || "")), [shown?.prompt_markdown]);

	// Test subjects for the selected prompt, when its module provides a test.
	const subjectsUrl = current && canEdit ? endpoints.subjects(current) : null;
	useEffect(() => {
		setSubjectId("");
		if (!subjectsUrl) return setSubjects(null);
		getAPICall(instance, accounts[0], subjectsUrl)
			.then((res) => setSubjects(res.data))
			.catch(() => setSubjects([]));
	}, [subjectsUrl]);

	const run = async (label, fn) => {
		setBusy(label);
		setError("");
		try {
			await fn();
		} catch (err) {
			setError(apiError(err, "Something went wrong."));
		} finally {
			setBusy("");
		}
	};

	const draftBody = () => ({
		prompt_markdown: draft.prompt_markdown,
		change_note: draft.change_note,
		ai_model_id: draft.ai_model_id,
		max_pages: draft.max_pages ?? "",
		max_tokens: draft.max_tokens ?? "",
		output_schema: schemaText,
	});

	const newDraft = () =>
		run("draft", async () => {
			const res = await postAPICall(instance, accounts[0], endpoints.createDraft, { from_id: version.id });
			await loadList(promptKey);
			setVersionId(res.data.version.id);
		});

	const saveDraft = () =>
		run("save", async () => {
			const res = await patchAPICall(instance, accounts[0], endpoints.draft(draft.id), draftBody());
			setVersion(res.data.version);
			setDraft({ ...res.data.version });
			await loadList(promptKey);
		});

	// Deletes the Draft and goes back to the Active version (loadList picks it).
	const discard = () =>
		run("discard", async () => {
			await deleteAPICall(instance, accounts[0], endpoints.draft(draft.id));
			setDiscardOpen(false);
			setDraft(null);
			await loadList(promptKey);
		});

	const activate = () =>
		run("activate", async () => {
			if (draft) await patchAPICall(instance, accounts[0], endpoints.draft(draft.id), draftBody());
			await postAPICall(instance, accounts[0], endpoints.activate(shown.id), {});
			await loadList(promptKey);
			setVersionId(shown.id);
		});

	const testIt = () =>
		run("test", async () => {
			if (draft) await patchAPICall(instance, accounts[0], endpoints.draft(draft.id), draftBody());
			setTestResult((await postAPICall(instance, accounts[0], endpoints.test(shown.id), { subject_id: subjectId })).data);
		});

	const download = () =>
		run("download", async () => {
			const blob = await getBlobAPICall(instance, accounts[0], endpoints.download(shown.id));
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${shown.prompt_key}.v${shown.version}.md`;
			a.click();
			URL.revokeObjectURL(url);
		});

	if (prompts && !prompts.length) return <div className="itBudgetEmpty">{error ? <span className="itBudgetError">{error}</span> : emptyText}</div>;
	if (!current || !shown) {
		return <div className="itBudgetEmpty">{error ? <span className="itBudgetError">{error}</span> : <CircularProgress />}</div>;
	}

	// Active models, plus the version's own model if it has since been made inactive.
	const modelChoices = models.filter((m) => m.active || m.id === shown.ai_model_id);

	return (
		<div className="itBudgetPromptLayout">
			<div className="itBudgetPromptNav">
				{!singleKey && <div className="itBudgetOverline">{listTitle}</div>}
				{!singleKey && prompts.map((p) => (
					<div key={p.prompt_key} className={`itBudgetPromptCard${p.prompt_key === promptKey ? " itBudgetPromptCard--active" : ""}`} onClick={() => pick(p)}>
						<div className="itBudgetMono">
							<b>{p.prompt_key}</b>
						</div>
						<div style={{ fontSize: 12.5 }}>{p.active_version ? `v${p.active_version.version} Active · ${p.active_version.model_name}` : "No Active version"}</div>
						<div className="itBudgetMuted">
							{[promptMeta ? promptMeta(p) : p.module, p.draft_version ? `Draft v${p.draft_version.version} open` : "", p.active ? "" : "Inactive"].filter(Boolean).join(" · ")}
						</div>
					</div>
				))}
				<div className="itBudgetOverline" style={singleKey ? undefined : { marginTop: 8 }}>
					Versions of {current.prompt_key}
				</div>
				{current.versions.map((v) => (
					<div key={v.id} className={`itBudgetPromptCard${v.id === shown.id ? " itBudgetPromptCard--active" : ""}`} onClick={() => setVersionId(v.id)}>
						<div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
							<b>v{v.version}</b>
							<span style={pillStyle(VERSION_PILLS[v.status])}>{v.status}</span>
						</div>
						<div className="itBudgetMuted">{v.change_note || "—"}</div>
						<div className="itBudgetMuted">{fmtDay(v.activated || v.modified)}</div>
					</div>
				))}
			</div>

			<div className="itBudgetPromptMain">
				<Collapse in={Boolean(error)}>
					<Alert severity="error" onClose={() => setError("")}>
						{error}
					</Alert>
				</Collapse>
				<div className="itBudgetCard" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
					<div style={{ marginRight: "auto" }}>
						<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
							<span className="itBudgetMono" style={{ fontSize: 18 }}>
								{shown.prompt_key}
							</span>
							<span style={pillStyle(VERSION_PILLS[shown.status])}>
								v{shown.version} {shown.status}
							</span>
						</div>
						<div className="itBudgetMuted">
							{current.name}
							{current.description ? ` — ${current.description}` : ""}
							{canEdit ? "" : " · View only"}
						</div>
					</div>
					{promptActions && promptActions(current)}
					<Button variant="outlined" startIcon={<DownloadIcon />} onClick={download} disabled={Boolean(busy)}>
						Download .md
					</Button>
					{canEdit && !draft && !current.draft_version && (
						<Button variant="outlined" startIcon={<EditNoteIcon />} onClick={newDraft} disabled={Boolean(busy)}>
							New Draft from v{shown.version}
						</Button>
					)}
					{canEdit && draft && (
						<Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDiscardOpen(true)} disabled={Boolean(busy)}>
							Discard Draft
						</Button>
					)}
					{canEdit && draft && (
						<Button variant="outlined" onClick={saveDraft} disabled={Boolean(busy)}>
							Save Draft
						</Button>
					)}
					{canEdit && shown.status !== "active" && (
						<Button variant="contained" startIcon={busy === "activate" ? <CircularProgress size={16} /> : <RocketLaunchIcon />} onClick={activate} disabled={Boolean(busy)}>
							Activate v{shown.version}
						</Button>
					)}
				</div>

				<div className="itBudgetEditorGrid">
					<div className="itBudgetCard">
						<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
							<span className="itBudgetOverline">Markdown</span>
							<span className="itBudgetMuted">{markdownHint}</span>
						</div>
						<TextField
							className="itBudgetMarkdownInput"
							multiline
							minRows={20}
							fullWidth
							value={shown.prompt_markdown}
							onChange={(e) => setDraft({ ...draft, prompt_markdown: e.target.value })}
							InputProps={{ readOnly: !draft }}
							inputProps={{ spellCheck: false, "aria-label": "Prompt Markdown" }}
						/>
					</div>
					<div className="itBudgetCard">
						<div className="itBudgetOverline">Preview</div>
						<div className="itBudgetMarkdownPreview" dangerouslySetInnerHTML={{ __html: preview }} />
					</div>
				</div>

				<div className="itBudgetCard itBudgetFormGrid">
					<TextField size="small" label="Change Note" required value={shown.change_note || ""} disabled={!draft} onChange={(e) => setDraft({ ...draft, change_note: e.target.value })} />
					<TextField select size="small" label="Model" value={modelChoices.length ? shown.ai_model_id : ""} disabled={!draft} onChange={(e) => setDraft({ ...draft, ai_model_id: e.target.value })}>
						{modelChoices.map((m) => (
							<MenuItem key={m.id} value={m.id}>
								{m.name} ({m.provider} · {m.model_identifier}){m.active ? "" : " — inactive"}
							</MenuItem>
						))}
					</TextField>
					<TextField size="small" label="Max Pages" value={shown.max_pages ?? ""} disabled={!draft} onChange={(e) => setDraft({ ...draft, max_pages: e.target.value })} />
					<TextField size="small" label="Max Tokens" value={shown.max_tokens ?? ""} disabled={!draft} onChange={(e) => setDraft({ ...draft, max_tokens: e.target.value })} helperText={draft ? "Empty: the caller's default" : ""} />
					<Button onClick={() => setSchemaOpen(true)}>
						{draft ? "Edit" : "View"} Output Schema (JSON){schemaText ? "" : " — none"}
					</Button>
				</div>

				{canEdit && subjects && (
					<div className="itBudgetCard" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
						<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
							<div style={{ marginRight: "auto" }}>
								<b>Test Result</b>
								<div className="itBudgetMuted">v{shown.version} run against an existing record; nothing is saved</div>
							</div>
							<TextField select size="small" label="Test against" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} sx={{ width: 340 }}>
								{subjects.map((s) => (
									<MenuItem key={s.id} value={s.id}>
										{s.label}
									</MenuItem>
								))}
							</TextField>
							<Button variant="outlined" startIcon={busy === "test" ? <CircularProgress size={16} /> : <ScienceIcon />} disabled={!subjectId || Boolean(busy)} onClick={testIt}>
								Test
							</Button>
						</div>
						{testResult && <TestResult result={testResult} />}
					</div>
				)}
			</div>

			<Dialog open={discardOpen} onClose={busy ? undefined : () => setDiscardOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle className="itBudgetDialogTitle">Discard Draft v{shown.version}?</DialogTitle>
				<DialogContent className="itBudgetDialogContent">
					<div style={{ fontSize: 14 }}>
						The Draft and all its edits are deleted. {current.active_version ? `The Active version (v${current.active_version.version}) stays as it is.` : ""}
					</div>
					<div className="itBudgetDialogButtons">
						<Button onClick={() => setDiscardOpen(false)} disabled={Boolean(busy)}>
							Keep Draft
						</Button>
						<Button variant="contained" color="error" onClick={discard} disabled={Boolean(busy)} startIcon={busy === "discard" ? <CircularProgress size={16} /> : null}>
							Discard
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={schemaOpen} onClose={() => setSchemaOpen(false)} fullWidth maxWidth="md">
				<DialogTitle className="itBudgetDialogTitle">
					Output schema · {shown.prompt_key} v{shown.version}
				</DialogTitle>
				<DialogContent className="itBudgetDialogContent">
					<div className="itBudgetMuted" style={{ marginBottom: 8 }}>
						A JSON object schema the answer must follow, or empty for a prompt without one.
					</div>
					<TextField className="itBudgetMarkdownInput" multiline minRows={18} fullWidth value={schemaText} onChange={(e) => setSchemaText(e.target.value)} InputProps={{ readOnly: !draft }} />
					<div className="itBudgetDialogButtons">
						<Button onClick={() => setSchemaOpen(false)}>Close</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};

// A field-by-field comparison when the test handler returns one (IT Budget's does); otherwise the raw answer.
const TestResult = ({ result }) => {
	if (!Array.isArray(result.diff)) {
		return <pre className="itBudgetMono" style={{ whiteSpace: "pre-wrap", fontSize: 12.5, margin: 0 }}>{JSON.stringify(result, null, 2)}</pre>;
	}
	return (
		<>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Field</TableCell>
						<TableCell>v{result.prompt?.version}</TableCell>
						<TableCell>{result.stored_label || "Stored"}</TableCell>
						<TableCell>Match</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{result.diff.map((d) => (
						<TableRow key={d.field}>
							<TableCell className="itBudgetMono">{d.field}</TableCell>
							<TableCell>{String(d.extracted ?? "—")}</TableCell>
							<TableCell>{String(d.stored ?? "—")}</TableCell>
							<TableCell>{d.same ? <CheckCircleIcon fontSize="small" style={{ color: "#2e7d32" }} /> : <b style={{ color: "#b45309" }}>differs</b>}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			{"schema_ok" in result && (
				<div style={{ fontSize: 13, color: result.schema_ok ? "#2e7d32" : "#c62828" }}>
					{result.schema_ok ? "Answer matches the output schema" : `Answer is missing: ${result.schema_missing.join(", ")}`}
					{result.review_reasons?.length ? ` · Would need review: ${result.review_reasons.join("; ")}` : ""}
				</div>
			)}
		</>
	);
};

export default PromptWorkspace;
